import { withinRange } from '../math/fixed.ts';
import type { BattleState, Rescued } from '../types.ts';
import { ev } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The pit in the woods (Caras prestadas 3, docs/SAGAS.md): the two who went missing are held in a pit among the roots.
 * A standing hero who stays by it frees one at a time, who follows that hero back to the node; nightmares near them
 * hurt them. Reaching the pit, the heroes find Ebbe's cart beside it.
 */

const clue = (s: BattleState, id: string, pid: string, x: number, y: number): void => {
  const c = (s.clues ??= { hold: [], found: [] });
  if (c.found.some((f) => f.id === id)) return;
  c.found.push({ id, pid, tick: s.tick });
  ev(s, { t: 'clue', id, pid, x, y });
};

export const rescueTick = (s: BattleState): void => {
  const r = s.rules?.rescue;
  if (!r || !s.started) return;
  const st = (s.rescue ??= { held: 0, left: r.people, people: [], saved: 0, lost: 0, nextId: 1, seen: false });
  const standing = s.heroes.filter((h) => h.downTicks === 0);
  // Ebbe's cart stands by the pit: whoever gets there first sees it.
  const near = standing.find((h) => withinRange(h.x, h.y, r.pit.x, r.pit.y, r.seen));
  if (near && !st.seen) {
    st.seen = true;
    clue(s, 'ebbeDead', near.pid, r.pit.x, r.pit.y);
  }
  // At the pit: one more freed every so long, following whoever pulled them out.
  const at = standing.find((h) => withinRange(h.x, h.y, r.pit.x, r.pit.y, r.free));
  st.held = at && st.left > 0 ? st.held + 1 : 0;
  if (at && st.left > 0 && st.held >= r.ticks) {
    st.held = 0;
    st.left--;
    const p: Rescued = { id: st.nextId++, x: r.pit.x, y: r.pit.y, hp: r.hp, maxHp: r.hp, pid: at.pid, facing: 0, moving: false };
    st.people.push(p);
    ev(s, { t: 'rescue', kind: 'free', x: p.x, y: p.y });
  }
  const keep: Rescued[] = [];
  // In a line behind their hero, one step apart, so they don't walk on top of each other.
  const behind = new Map<string, number>();
  for (const p of st.people) {
    // Home: within reach of the node.
    if (withinRange(p.x, p.y, 0, 0, r.home)) {
      st.saved++;
      ev(s, { t: 'rescue', kind: 'safe', x: p.x, y: p.y });
      continue;
    }
    // Nightmares close by hurt them, each one now and then.
    if (s.tick % r.hurt.every === 0) for (const e of s.enemies) if (e.hp > 0 && e.kind !== 'boneWall' && withinRange(e.x, e.y, p.x, p.y, r.hurt.range)) p.hp -= r.hurt.damage;
    if (p.hp <= 0) {
      st.lost++;
      ev(s, { t: 'rescue', kind: 'down', x: p.x, y: p.y });
      continue;
    }
    // After their hero (or the nearest standing one if theirs fell), a step behind.
    const h = standing.find((x) => x.pid === p.pid) ?? standing[0];
    const n = h ? (behind.get(h.pid) ?? 0) : 0;
    if (h) behind.set(h.pid, n + 1);
    p.moving = !!h && !withinRange(h.x, h.y, p.x, p.y, r.follow + n * r.follow);
    if (h && p.moving) stepToward(p, h.x, h.y, r.speed);
    keep.push(p);
  }
  st.people = keep;
};

/** Won once everyone is home; lost if one of them falls. */
export const rescueResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.rescue;
  const st = s.rescue;
  if (!r || !st) return null;
  if (st.lost) return 'lost';
  return st.saved >= r.people ? 'won' : null;
};
